import React, { useState, useEffect } from 'react'
import * as XLSX from 'xlsx'
import { Download, Eye, RotateCcw, XCircle, Play, Pause, FileText } from 'lucide-react'
import InvoiceGenerator from '../components/InvoiceGenerator'
import { adminAPI } from '../services/api'

interface Payment {
  id: number
  user_id: number
  user_email: string
  user_name: string
  amount: number
  currency: string
  status: 'pending' | 'completed' | 'failed' | 'refunded'
  payment_method: string
  stripe_payment_id?: string
  created_at: string
  plan_name?: string
  subscription_id?: string
  subscription_status?: 'active' | 'canceled' | 'pending' | 'expired'
  next_billing?: string
}

interface Subscription {
  id: string
  user_id: number
  user_email: string
  user_name: string
  plan_name: string
  status: 'active' | 'canceled' | 'pending' | 'expired'
  current_period_start: string
  current_period_end: string
  amount: number
  currency: string
  stripe_subscription_id?: string
}

interface PaymentStats {
  totalRevenue: number
  monthlyRevenue: number
  totalTransactions: number
  activeSubscriptions: number
  trialConversions: number
  refundRate: number
}

const AdminPayments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([])
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [stats, setStats] = useState<PaymentStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedPeriod, setSelectedPeriod] = useState('30') // days
  const [activeTab, setActiveTab] = useState<'payments' | 'subscriptions'>('payments')
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [refundLoading, setRefundLoading] = useState<number | null>(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPaymentForInvoice, setSelectedPaymentForInvoice] = useState<Payment | null>(null)
  const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false)

  useEffect(() => {
    fetchPaymentData()
  }, [selectedPeriod])

  const fetchPaymentData = async () => {
    try {
      setLoading(true)
      
      // Fetch all payments from admin API
      const response = await adminAPI.getPayments()
      const allPayments = response.data || []
      
      // Debug: sprawdź strukturę danych
      console.log('Admin payments response:', response)
      console.log('First payment:', allPayments[0])
      
      // Calculate stats from real data
      const now = new Date()
      const currentMonth = now.getMonth()
      const currentYear = now.getFullYear()
      
      const monthlyPayments = allPayments.filter((p: any) => {
        const date = new Date(p.created_at)
        return date.getMonth() === currentMonth && date.getFullYear() === currentYear && p.status === 'completed'
      })
      
      const totalRevenue = allPayments
        .filter((p: any) => p.status === 'completed')
        .reduce((sum: number, p: any) => sum + (parseFloat(p.amount) || 0), 0)
      
      const monthlyRevenue = monthlyPayments
        .reduce((sum: number, p: any) => sum + (parseFloat(p.amount) || 0), 0)
      
      const refundedCount = allPayments.filter((p: any) => p.status === 'refunded').length
      const refundRate = allPayments.length > 0 ? (refundedCount / allPayments.length) * 100 : 0
      
      // Map API payments to component Payment format
      const mappedPayments = allPayments.map((p: any) => {
        // Określ plan na podstawie kwoty jeśli brak plan_name
        let planName = p.plan_name
        if (!planName) {
          if (p.amount === 4.99 || p.amount === 24.99) {
            planName = 'Standard'
          } else if (p.amount === 7.99 || p.amount === 59.99) {
            planName = 'Pro'
          } else if (p.amount === 699 || p.amount === 699.00) {
            planName = 'Unlimited'
          }
        }
        
        return {
          id: p.id,
          user_id: p.user_id,
          user_email: p.user_email || 'N/A',
          user_name: p.user_name || 'N/A',
          amount: p.amount,
          currency: p.currency,
          status: p.status,
          payment_method: p.payment_method,
          stripe_payment_id: p.stripe_payment_intent_id,
          created_at: p.created_at,
          plan_name: planName || 'N/A'
        }
      })
      
      // Policz aktywne licencje jako subskrypcje
      const activeLicenses = allPayments.filter((p: any) => p.status === 'completed').length
      
      setPayments(mappedPayments)
      setSubscriptions([]) // TODO: Add subscriptions when API is ready
      setStats({
        totalRevenue,
        monthlyRevenue,
        totalTransactions: allPayments.length,
        activeSubscriptions: activeLicenses, // Liczba ukończonych płatności = aktywne licencje
        trialConversions: 0, // TODO: Calculate from real data
        refundRate: Math.round(refundRate * 100) / 100
      })
    } catch (err) {
      setError('Failed to fetch payment data')
      console.error('Payment data fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  // Export to Excel function
  const exportToExcel = () => {
    const fileName = activeTab === 'payments' ? 'payments_export' : 'subscriptions_export'
    
    let worksheetData
    if (activeTab === 'payments') {
      worksheetData = filteredPayments.map((payment: Payment) => ({
        'ID': payment.id,
        'User Email': payment.user_email,
        'User Name': payment.user_name,
        'Amount': payment.amount,
        'Currency': payment.currency,
        'Status': payment.status,
        'Payment Method': payment.payment_method,
        'Plan': payment.plan_name || 'N/A',
        'Created Date': formatDate(payment.created_at),
        'Stripe ID': payment.stripe_payment_id || 'N/A'
      }))
    } else {
      worksheetData = filteredSubscriptions.map((sub: Subscription) => ({
        'Subscription ID': sub.id,
        'User Email': sub.user_email,
        'User Name': sub.user_name,
        'Plan': sub.plan_name,
        'Status': sub.status,
        'Amount': sub.amount,
        'Currency': sub.currency,
        'Period Start': formatDate(sub.current_period_start),
        'Period End': formatDate(sub.current_period_end),
        'Stripe ID': sub.stripe_subscription_id || 'N/A'
      }))
    }
    
    const worksheet = XLSX.utils.json_to_sheet(worksheetData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, activeTab === 'payments' ? 'Payments' : 'Subscriptions')
    
    const today = new Date().toISOString().split('T')[0]
    XLSX.writeFile(workbook, `${fileName}_${today}.xlsx`)
  }

  // Refund payment function
  const handleRefund = async (paymentId: number) => {
    if (!window.confirm('Czy na pewno chcesz zwrócić tę płatność?')) {
      return
    }
    
    setRefundLoading(paymentId)
    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 2000)) // Simulate API call
      
      // Update payment status locally
      setPayments(prev => 
        prev.map(payment => 
          payment.id === paymentId 
            ? { ...payment, status: 'refunded' as const }
            : payment
        )
      )
      
      alert('Płatność została zwrócona pomyślnie')
    } catch (error) {
      alert('Błąd podczas zwracania płatności')
    } finally {
      setRefundLoading(null)
    }
  }

  // Manage subscription function
  const handleSubscriptionAction = async (subscriptionId: string, action: 'activate' | 'cancel') => {
    const actionText = action === 'activate' ? 'aktywować' : 'anulować'
    if (!window.confirm(`Czy na pewno chcesz ${actionText} tę subskrypcję?`)) {
      return
    }
    
    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1500)) // Simulate API call
      
      // Update subscription status locally
      setSubscriptions(prev => 
        prev.map(sub => 
          sub.id === subscriptionId 
            ? { ...sub, status: action === 'activate' ? 'active' as const : 'canceled' as const }
            : sub
        )
      )
      
      alert(`Subskrypcja została ${action === 'activate' ? 'aktywowana' : 'anulowana'} pomyślnie`)
    } catch (error) {
      alert(`Błąd podczas ${action === 'activate' ? 'aktywacji' : 'anulowania'} subskrypcji`)
    }
  }

  // View payment details
  const handleViewPayment = (payment: Payment) => {
    setSelectedPayment(payment)
    setShowPaymentModal(true)
  }

  // Generate invoice
  const handleGenerateInvoice = (payment: Payment) => {
    setSelectedPaymentForInvoice(payment)
    setShowInvoiceGenerator(true)
  }

  const closeInvoiceGenerator = () => {
    setShowInvoiceGenerator(false)
    setSelectedPaymentForInvoice(null)
  }

  // Filter functions
  const filteredPayments = payments.filter(payment => {
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter
    const matchesSearch = payment.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (payment.plan_name && payment.plan_name.toLowerCase().includes(searchTerm.toLowerCase()))
    return matchesStatus && matchesSearch
  })

  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter
    const matchesSearch = sub.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sub.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sub.plan_name.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesStatus && matchesSearch
  })

  const getStatusColor = (status: Payment['status'] | Subscription['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      case 'refunded':
        return 'bg-gray-100 text-gray-800'
      case 'canceled':
        return 'bg-red-100 text-red-800'
      case 'expired':
        return 'bg-orange-100 text-orange-800'
      case 'active':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 p-6">
        <div className="flex items-center justify-center">
          <div className="text-white">Ładowanie danych płatności...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-dark-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-4">Zarządzanie Płatnościami</h1>
          <div className="flex flex-wrap items-center gap-4">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="bg-dark-700 text-white border border-dark-600 rounded-lg px-4 py-2"
            >
              <option value="7">Ostatnie 7 dni</option>
              <option value="30">Ostatnie 30 dni</option>
              <option value="90">Ostatnie 90 dni</option>
              <option value="365">Ostatni rok</option>
            </select>
            
            <input
              type="text"
              placeholder="Szukaj po email, nazwie lub planie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-dark-700 text-white border border-dark-600 rounded-lg px-4 py-2 min-w-64"
            />
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-dark-700 text-white border border-dark-600 rounded-lg px-4 py-2"
            >
              <option value="all">Wszystkie statusy</option>
              <option value="completed">Zakończone</option>
              <option value="pending">Oczekujące</option>
              <option value="failed">Nieudane</option>
              <option value="refunded">Zwrócone</option>
              {activeTab === 'subscriptions' && (
                <>
                  <option value="active">Aktywne</option>
                  <option value="canceled">Anulowane</option>
                  <option value="expired">Wygasłe</option>
                </>
              )}
            </select>
            
            <button
              onClick={exportToExcel}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Eksport Excel
            </button>
            
            <button
              onClick={fetchPaymentData}
              className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg transition-colors"
            >
              Odśwież
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-dark-600">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('payments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'payments'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                Płatności
              </button>
              <button
                onClick={() => setActiveTab('subscriptions')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'subscriptions'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                Subskrypcje
              </button>
            </nav>
          </div>
        </div>

        {error && (
          <div className="bg-red-600 text-white p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
            <div className="bg-dark-800 p-6 rounded-lg">
              <h3 className="text-gray-400 text-sm font-medium">Łączny przychód</h3>
              <p className="text-2xl font-bold text-white">{formatCurrency(stats.totalRevenue, 'EUR')}</p>
            </div>
            <div className="bg-dark-800 p-6 rounded-lg">
              <h3 className="text-gray-400 text-sm font-medium">Przychód miesięczny</h3>
              <p className="text-2xl font-bold text-green-400">{formatCurrency(stats.monthlyRevenue, 'EUR')}</p>
            </div>
            <div className="bg-dark-800 p-6 rounded-lg">
              <h3 className="text-gray-400 text-sm font-medium">Łączne transakcje</h3>
              <p className="text-2xl font-bold text-white">{stats.totalTransactions}</p>
            </div>
            <div className="bg-dark-800 p-6 rounded-lg">
              <h3 className="text-gray-400 text-sm font-medium">Aktywne subskrypcje</h3>
              <p className="text-2xl font-bold text-blue-400">{stats.activeSubscriptions}</p>
            </div>
            <div className="bg-dark-800 p-6 rounded-lg">
              <h3 className="text-gray-400 text-sm font-medium">Konwersje trial</h3>
              <p className="text-2xl font-bold text-purple-400">{stats.trialConversions}</p>
            </div>
            <div className="bg-dark-800 p-6 rounded-lg">
              <h3 className="text-gray-400 text-sm font-medium">Odsetek zwrotów</h3>
              <p className="text-2xl font-bold text-yellow-400">{stats.refundRate}%</p>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="bg-dark-800 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-dark-600">
            <h2 className="text-xl font-semibold text-white">
              {activeTab === 'payments' ? 'Płatności' : 'Subskrypcje'}
              <span className="text-gray-400 text-sm ml-2">
                ({activeTab === 'payments' ? filteredPayments.length : filteredSubscriptions.length} pozycji)
              </span>
            </h2>
          </div>
          <div className="overflow-x-auto">
            {activeTab === 'payments' ? (
              <table className="w-full">
                <thead className="bg-dark-700">
                  <tr>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Użytkownik</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Plan</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Kwota</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Status</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Metoda</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Data</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment) => (
                    <tr key={payment.id} className="border-b border-dark-600 hover:bg-dark-700">
                      <td className="py-4 px-6">
                        <div>
                          <div className="text-white font-medium">{payment.user_name}</div>
                          <div className="text-gray-400 text-sm">{payment.user_email}</div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-300">
                        {payment.plan_name || 'N/A'}
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-white font-medium">
                          {formatCurrency(payment.amount, payment.currency)}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                          {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-gray-300">
                        {payment.payment_method.replace('stripe_', '').toUpperCase()}
                      </td>
                      <td className="py-4 px-6 text-gray-300">
                        {formatDate(payment.created_at)}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleViewPayment(payment)}
                            className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                          >
                            <Eye className="h-3 w-3" />
                            Szczegóły
                          </button>
                          <button 
                            onClick={() => handleGenerateInvoice(payment)}
                            className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1"
                          >
                            <FileText className="h-3 w-3" />
                            Faktura
                          </button>
                          {payment.status === 'completed' && (
                            <button 
                              onClick={() => handleRefund(payment.id)}
                              disabled={refundLoading === payment.id}
                              className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1 disabled:opacity-50"
                            >
                              <RotateCcw className={`h-3 w-3 ${refundLoading === payment.id ? 'animate-spin' : ''}`} />
                              {refundLoading === payment.id ? 'Zwracanie...' : 'Zwróć'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full">
                <thead className="bg-dark-700">
                  <tr>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Użytkownik</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Plan</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Status</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Kwota</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Okres</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Następna płatność</th>
                    <th className="text-left py-3 px-6 text-gray-300 font-medium">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscriptions.map((subscription) => (
                    <tr key={subscription.id} className="border-b border-dark-600 hover:bg-dark-700">
                      <td className="py-4 px-6">
                        <div>
                          <div className="text-white font-medium">{subscription.user_name}</div>
                          <div className="text-gray-400 text-sm">{subscription.user_email}</div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-300">
                        {subscription.plan_name}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(subscription.status)}`}>
                          {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-white font-medium">
                          {formatCurrency(subscription.amount, subscription.currency)}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-gray-300">
                        <div className="text-sm">
                          <div>Od: {formatDate(subscription.current_period_start)}</div>
                          <div>Do: {formatDate(subscription.current_period_end)}</div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-300">
                        {subscription.status === 'active' ? formatDate(subscription.current_period_end) : 'N/A'}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex space-x-2">
                          {subscription.status === 'active' ? (
                            <button 
                              onClick={() => handleSubscriptionAction(subscription.id, 'cancel')}
                              className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
                            >
                              <Pause className="h-3 w-3" />
                              Anuluj
                            </button>
                          ) : subscription.status === 'canceled' ? (
                            <button 
                              onClick={() => handleSubscriptionAction(subscription.id, 'activate')}
                              className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1"
                            >
                              <Play className="h-3 w-3" />
                              Aktywuj
                            </button>
                          ) : (
                            <span className="text-gray-500 text-sm">Brak akcji</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {((activeTab === 'payments' && filteredPayments.length === 0) || 
          (activeTab === 'subscriptions' && filteredSubscriptions.length === 0)) && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg">
              {activeTab === 'payments' 
                ? 'Nie znaleziono płatności dla wybranych kryteriów' 
                : 'Nie znaleziono subskrypcji dla wybranych kryteriów'
              }
            </div>
          </div>
        )}
        
        {/* Payment Details Modal */}
        {showPaymentModal && selectedPayment && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowPaymentModal(false)}></div>
              </div>
              
              <div className="inline-block align-bottom bg-dark-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-dark-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-white">Szczegóły płatności</h3>
                    <button
                      onClick={() => setShowPaymentModal(false)}
                      className="text-gray-400 hover:text-white"
                    >
                      <XCircle className="h-6 w-6" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400">ID Płatności</label>
                        <p className="text-white">{selectedPayment.id}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">Status</label>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedPayment.status)}`}>
                          {selectedPayment.status.charAt(0).toUpperCase() + selectedPayment.status.slice(1)}
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-400">Użytkownik</label>
                      <p className="text-white">{selectedPayment.user_name}</p>
                      <p className="text-gray-400 text-sm">{selectedPayment.user_email}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400">Kwota</label>
                        <p className="text-white font-semibold">{formatCurrency(selectedPayment.amount, selectedPayment.currency)}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">Plan</label>
                        <p className="text-white">{selectedPayment.plan_name || 'N/A'}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400">Metoda płatności</label>
                        <p className="text-white">{selectedPayment.payment_method.replace('stripe_', '').toUpperCase()}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">Data utworzenia</label>
                        <p className="text-white">{formatDate(selectedPayment.created_at)}</p>
                      </div>
                    </div>
                    
                    {selectedPayment.stripe_payment_id && (
                      <div>
                        <label className="block text-sm font-medium text-gray-400">Stripe Payment ID</label>
                        <p className="text-white font-mono text-sm">{selectedPayment.stripe_payment_id}</p>
                      </div>
                    )}
                    
                    {selectedPayment.subscription_id && (
                      <div>
                        <label className="block text-sm font-medium text-gray-400">Subskrypcja</label>
                        <p className="text-white">{selectedPayment.subscription_id}</p>
                        {selectedPayment.next_billing && (
                          <p className="text-gray-400 text-sm">Następna płatność: {formatDate(selectedPayment.next_billing)}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-dark-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="w-full inline-flex justify-center rounded-md border border-gray-600 shadow-sm px-4 py-2 bg-dark-600 text-base font-medium text-white hover:bg-dark-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Zamknij
                  </button>
                  {selectedPayment.status === 'completed' && (
                    <button
                      onClick={() => {
                        setShowPaymentModal(false)
                        handleRefund(selectedPayment.id)
                      }}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-red-600 shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Zwróć płatność
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Invoice Generator Modal */}
        {selectedPaymentForInvoice && (
          <InvoiceGenerator
            payment={selectedPaymentForInvoice}
            isOpen={showInvoiceGenerator}
            onClose={closeInvoiceGenerator}
          />
        )}
      </div>
    </div>
  )
}

export default AdminPayments